import { defineStore } from 'pinia'
import { authService } from '@/api/auth'
import { useLoadingStore } from '@/stores/loading'

interface User {
  id: string
  email: string
  first_name: string
  last_name: string
  username: string
  role: 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE'
  owner_id: string | null
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    isAuthenticated: false,
  }),
  actions: {
    async login(email: string, password: string) {
      const loading = useLoadingStore()
      loading.start()
      try {
        const data = await authService.login(email, password)
        localStorage.setItem('accessToken', data.access)
        localStorage.setItem('refreshToken', data.refresh)
        this.user = data.user
        this.isAuthenticated = true
        return data.user.role
      } catch (error) {
        console.error('Login failed:', error)
        throw error
      } finally {
        loading.stop()
      }
    },

    async register(userData: {
      username: string
      email: string
      password: string
      confirmPassword: string
      first_name: string
      last_name: string
    }) {
      try {
        return await authService.register(userData)
      } catch (error) {
        console.error('Registration failed:', error)
        throw error
      }
    },

    async logout() {
      const loading = useLoadingStore()
      loading.start()
      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          await authService.logout(refreshToken)
        }
      } catch (error) {
        console.error('Logout failed:', error)
      } finally {
        this.clearAuth()
        loading.stop()
        // Force redirect to login
        window.location.href = '/login'
      }
    },

    clearAuth() {
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      this.user = null
      this.isAuthenticated = false
    },

    async fetchUserProfile() {
      const loading = useLoadingStore()
      loading.start()
      try {
        const data = await authService.getUserProfile()
        this.user = data
        this.isAuthenticated = true
        return data
      } catch (error) {
        this.clearAuth()
        throw error
      } finally {
        loading.stop()
      }
    },

    async checkAuth(): Promise<void> {
      const accessToken = localStorage.getItem('accessToken')
      const refreshToken = localStorage.getItem('refreshToken')

      if (!accessToken || !refreshToken) {
        this.clearAuth()
        return
      }

      try {
        const data = await authService.getUserProfile()
        this.user = data
        this.isAuthenticated = true
      } catch (error) {
        // If profile fails, do nothing—401 will be handled by axios
        this.clearAuth()
        console.log(error)
      }
    },

    async requestPasswordReset(email: string) {
      const loading = useLoadingStore()
      loading.start()
      try {
        return await authService.requestPasswordReset(email)
      } finally {
        loading.stop()
      }
    },

    async confirmPasswordReset(data: { uid: string; token: string; password: string; confirmPassword: string }) {
      const loading = useLoadingStore()
      loading.start()
      try {
        return await authService.confirmPasswordReset(data)
      } finally {
        loading.stop()
      }
    },
  },
})

